import { ActivityNote, FormData } from "0.Mine/0.Components/CRM/Leads/sharedTypes";
import { Business } from "0.Mine/3.Interfaces/business";

import { useSession } from "../../2.Context/sessionContext";
import { GeminiReviewRequest } from "../../3.Interfaces/GeminiReviewRequest";
import { GeminiSocialPostRequest } from "../../3.Interfaces/GeminiSocialPostRequest";
import axiosInstance from "../../axiosInstance";
import { buildLeadAnalysisPrompt } from "../utils/ai";

export const UseCreateGeminiRequest = () => {
  const { session } = useSession();
  const token = session?.user.token;
  const id = Number(session?.user.id);

  const createReviewWithGemini = async (request: GeminiReviewRequest) => {
    const prompt = `
        You are ${request.ownerFirstName}, owner of ${request.name}, a business specializing in ${request.about} within the ${request.category} category.  You're crafting a thoughtful response to a customer review.
        
        Here are the review details:
        
        *   **Review:** ${request.review}
        *   **Stars:** ${request.reviewCount} out of 5
        *   **Title:** ${request.reviewTitle}
        *   **User:** ${request.userId}
        
        Your response should be professional, courteous, and demonstrate that you value customer feedback.  Thank the reviewer by name for their input.  Strive to understand their perspective.
        
        However, your response should also be firm and defend your business's reputation if the review is unfair or contains inaccuracies.  Be prepared to address specific points if the review is detailed.  If the review is very short and seems potentially fake or spam, you can politely acknowledge it while suggesting they elaborate for a more constructive dialogue.  Maintain a professional tone throughout.
        
        Specifically:
        
        *   **Acknowledge and thank:** Start by thanking ${request.userId} for their review.
        *   **Show empathy:** Demonstrate that you understand their concern, even if you disagree.
        *   **Address specifics (if applicable):** If the review is detailed, respond to individual points concisely and directly.
        *   **Offer a solution (if possible):** If appropriate, suggest a way to resolve the issue or continue the conversation offline.
        *   **Defend your business (if necessary):**  If the review contains false information, politely and professionally correct it.  Focus on facts, not emotions.
        *   **Maintain professionalism:**  Avoid getting defensive or emotional, even if the review is negative.
        
        Keep the response concise and focused.  Do not include any placeholders for contact information or website addresses.  Only use the information provided in the review details.  The entire response should be contained within the text, with no additional commentary or explanation outside of the reply itself.
        `;

    const payloadForQuery: Record<string, string> = {
      id: id.toString(),
      token: token ?? "",
      prompt: prompt,
      action: "generatetext",
    };

    // Convert payload object into a query string
    const queryString = new URLSearchParams(payloadForQuery).toString();

    try {
      // Append query string to your endpoint URL
      const response = await axiosInstance.post(`GET/gemini.php?${queryString}`);
      console.log(response.data);
      return response.data.content;
    } catch (exception) {
      console.error(exception);
    }
  };

  const CreaSocialMediaPostWithGemini = async (request: GeminiSocialPostRequest) => {
    const prompt = `
        You are ${request.business.ownerFirstName}, owner of ${request.business.name}, a business with the following description: "${request.business.about}".  
        You're crafting a social media post to promote your business on ${request.socialMedia}.
        In your social media post, you aim to provide value to promote your business forward. 
        On top of that, adapt this response to this custom prompt:
        ${request.prompt}

        More details on the post you are supposed to create include the following:
        *   **Post Type:** ${request.postType}
        *   **Content:** ${request.content}
        *  **Media:** ${request.media}
        *  **Scheduled Date:** ${request.scheduledDate}
        * **Restraints:** ${request.restraints}
        * Keep it concise, every word matters. Use simple language to adapt to the target audience.
        * Avoid using any placeholders for contact information or website addresses. Only use the information provided in the review details.
        * The entire response should be contained within the text, with no additional commentary or explanation outside of the reply itself.
        * Make sure to include 3 competitive hashtags to increase visibility.
        `;

    const payloadForQuery: Record<string, string> = {
      id: id.toString(),
      token: token ?? "",
      prompt: prompt,
      action: "generatetext",
    };

    // Convert payload object into a query string
    const queryString = new URLSearchParams(payloadForQuery).toString();

    try {
      // Append query string to your endpoint URL
      const response = await axiosInstance.post(`GET/gemini.php?${queryString}`);
      console.log(response.data);
      return response.data.content;
    } catch (exception) {
      console.error(exception);
    }
  };

  const createImageWithGemini = async (
    businessId: string,
    initialPrompt: string,
    imageDescription: string
  ) => {
    const prompt = ` 
        You are researching images on pexels. Your job is to construct and return the best possible words to query a precise image relevant for social media posts. You gotta start from this prompt and from this text:
        ${initialPrompt} and ${imageDescription}.
        Your answer should be a JSON object with the following format: {prompt: (prompt), description: (description)}. Limit your answer to 10 words. Only respond with the json and nothing else, absolutely no decorators or similar.
        `;

    const payloadForQuery: Record<string, string> = {
      id: id.toString(),
      token: token ?? "",
      prompt: prompt,
      action: "generatetext",
    };

    // Convert payload object into a query string
    const queryString = new URLSearchParams(payloadForQuery).toString();

    try {
      // Append query string to your endpoint URL
      const response = await axiosInstance.post(`GET/gemini.php?${queryString}`);
      console.log(response.data);
      return response.data.content;
    } catch (exception) {
      console.error(exception);
    }
  };

  const generateWorkFlowWithGemini = async (
    business: Business,
    numberOfSteps: number,
    tone: string,
    goal: string
  ) => {
    const businessDescription = `The user is ${business.ownerFirstName}, owner of ${business.name}, a business specializing in ${business.about} within the ${business.category} category.`;
    const prompt = buildWorkflowPrompt(goal, tone, businessDescription);

    const payloadForQuery: Record<string, string> = {
      id: id.toString(),
      token: token ?? "",
      prompt: prompt,
      action: "generatetext",
    };

    const queryString = new URLSearchParams(payloadForQuery).toString();

    try {
      // Append query string to your endpoint URL
      const response = await axiosInstance.post(`GET/gemini.php?${queryString}`);
      console.log(response.data);
      return response.data.content;
    } catch (exception) {
      console.error(exception);
    }
  };

  const analyzeLeadWithGemini = async (
    businessId: string,
    formdata: FormData,
    activities: ActivityNote[]
  ) => {
    const prompt = buildLeadAnalysisPrompt(formdata, activities);

    const payloadForQuery: Record<string, string> = {
      id: id.toString(),
      token: token ?? "",
      prompt: prompt,
      action: "generatetext",
    };

    // Convert payload object into a query string
    const queryString = new URLSearchParams(payloadForQuery).toString();

    try {
      // Append query string to your endpoint URL
      const response = await axiosInstance.post(`GET/gemini.php?${queryString}`);
      console.log(response.data);
      return response.data.content;
    } catch (exception) {
      console.error(exception);
    }
  };

  return {
    createReviewWithGemini,
    CreaSocialMediaPostWithGemini,
    createImageWithGemini,
    generateWorkFlowWithGemini,
    analyzeLeadWithGemini,
  };
};

export const UseFetchInformationWithGemini = () => {
  const { session } = useSession();
  const token = session?.user.token;
  const id = Number(session?.user.id);

  const fetchInfromationWithGemini = async (website: string) => {
    //strong anxiety. it's  03/03/2025, 10.09am. I hope this pays off. Working so fucking hard all the time.

    const prompt = `
        Given this website (${website}), create a brief description of the company's service or products after browsing its entire content. Ensure your information is correct by checking twice. Also try to find an associated email address, ideally in the contact us section.
        Return your answer as a JSON in the following format: {content: (content), contacts: (contact)}. Limit your answer to 300 words. Only respond with the json and nothing else
        `;

    const payloadForQuery: Record<string, string> = {
      id: id.toString(),
      token: token ?? "",
      prompt: prompt,
      action: "generatetext",
    };

    // Convert payload object into a query string
    const queryString = new URLSearchParams(payloadForQuery).toString();

    try {
      // Append query string to your endpoint URL
      const response = await axiosInstance.post(`GET/gemini.php?${queryString}`);
      const jsonObject = extractJsonFromString(response.data.content);
      console.log("JSON OBJECT IS ", jsonObject);
      return jsonObject;
    } catch (exception) {
      console.error(exception);
    }
  };
  const fetchPrimaryInformationWithGemini = async (website: string) => {
    //strong anxiety. it's  03/03/2025, 10.09am. I hope this pays off. Working so fucking hard all the time.

    const prompt = `
        Given this website (${website}), extract the name and whether or not the business operates as a service or a retail.
        Return your answer as a JSON in the following format: {name: (name of the business), type: (retail or service)}. Limit your answer to 300 words. Only respond with the json and nothing else, absolutely no decorators or similar.
        `;

    const payloadForQuery: Record<string, string> = {
      id: id.toString(),
      token: token ?? "",
      prompt: prompt,
      action: "generatetext",
    };

    // Convert payload object into a query string
    const queryString = new URLSearchParams(payloadForQuery).toString();

    try {
      // Append query string to your endpoint URL
      const response = await axiosInstance.post(`GET/gemini.php?${queryString}`);
      console.log(response.data.content);
      const jsonObject = extractJsonFromString(response.data.content);
      return jsonObject;
    } catch (exception) {
      console.error(exception);
    }
  };

  return { fetchInfromationWithGemini, fetchPrimaryInformationWithGemini };
};

export function extractJsonFromString(input: string): any {
  // Trim leading/trailing whitespace
  input = input.trim();

  // Remove markdown code block markers if present
  if (input.startsWith("```json")) {
    input = input.replace(/^```json\s*/, "");
  }
  if (input.endsWith("```")) {
    input = input.replace(/\s*```$/, "");
  }

  // Find the first occurrence of '{' and the last occurrence of '}'
  const jsonStart = input.indexOf("{");
  const jsonEnd = input.lastIndexOf("}");

  if (jsonStart === -1 || jsonEnd === -1) {
    throw new Error("No JSON object found in the string.");
  }

  // Extract the JSON substring and parse it
  const jsonString = input.substring(jsonStart, jsonEnd + 1);

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    throw new Error("Failed to parse JSON: " + error);
  }
}

export function buildWorkflowPrompt(goal: string, tone: string, userDescription: string): string {
  return `You are an expert business workflow generator.
  
  Generate a JSON object matching this exact TypeScript interface. Ensure that the texts are in Italian and that the JSON is valid. Do not include any additional text or explanations. The JSON object should look like this:
  
  export interface WorkFlow {
    templateId: number;
    templateType: string; // one of email, whatsapp, sms, mixed"
    templateName: string;
    steps: Step[];
    settings: Settings;
    created: string;
  }
  
  export interface Step {
    id: number;
    title: string;
    description: string;
    type: string;         // one of "email", "whatsapp", "sms"
    content: string;
    subject: string;
    delay: number;
    delayUnit: string;    // one of "hours", "minutes", "days"
    position: number;
  }
  
  export interface Settings {
    activateImmediately: boolean; // always true
    sendReports: boolean;        // always true
  }
  
  User description: "${userDescription}"
  User goal: "${goal}"
  Desired tone: "${tone}"
  
  Requirements:
  - Produce exactly 3–5 steps.
  - Each step’s \`type\` must be one of: "email", "whatsapp", "sms".
  - \`delayUnit\` must be one of: "hours", "minutes", "days".
  - \`settings.activateImmediately\` and \`settings.sendReports\` must both be true.
  - Use realistic delays.
  - Use ISO 8601 format for the \`created\` timestamp.
  - Return only the JSON object (no explanations, no extra text).`;
}
